<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

import r2StorageConfigAPI from 'dashboard/api/r2StorageConfig';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const isLoading = ref(false);
const isSaving = ref(false);
const isDisabling = ref(false);
const activeConfig = ref(null);

const form = reactive({
  bucket: '',
  endpoint: '',
  region: 'auto',
  publicBaseUrl: '',
  forcePathStyle: false,
  accessKeyId: '',
  secretAccessKey: '',
});

const isEnabled = computed(() => !!activeConfig.value);
const hasSavedAccessKey = computed(
  () => !!activeConfig.value?.access_key_id_present
);
const hasSavedSecretKey = computed(
  () => !!activeConfig.value?.secret_access_key_present
);

const formattedUpdatedAt = computed(() => {
  if (!activeConfig.value?.updated_at) {
    return '-';
  }

  const updatedAt = new Date(activeConfig.value.updated_at);
  if (Number.isNaN(updatedAt.getTime())) {
    return '-';
  }

  const pad = value => String(value).padStart(2, '0');
  return (
    [
      updatedAt.getFullYear(),
      pad(updatedAt.getMonth() + 1),
      pad(updatedAt.getDate()),
    ].join('/') + ` ${pad(updatedAt.getHours())}:${pad(updatedAt.getMinutes())}`
  );
});

const syncForm = config => {
  activeConfig.value = config;
  form.bucket = config?.bucket || '';
  form.endpoint = config?.endpoint || '';
  form.region = config?.region || 'auto';
  form.publicBaseUrl = config?.public_base_url || '';
  form.forcePathStyle = config?.force_path_style || false;
  form.accessKeyId = '';
  form.secretAccessKey = '';
};

const resetForm = () => {
  activeConfig.value = null;
  form.bucket = '';
  form.endpoint = '';
  form.region = 'auto';
  form.publicBaseUrl = '';
  form.forcePathStyle = false;
  form.accessKeyId = '';
  form.secretAccessKey = '';
};

const loadConfig = async () => {
  isLoading.value = true;
  try {
    const { data } = await r2StorageConfigAPI.get();
    syncForm(data.config);
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) ||
        t('R2_STORAGE_SETTINGS.MESSAGES.LOAD_FAILED')
    );
  } finally {
    isLoading.value = false;
  }
};

const buildPayload = () => {
  const payload = {
    bucket: form.bucket.trim(),
    endpoint: form.endpoint.trim(),
    region: form.region.trim() || 'auto',
    public_base_url: form.publicBaseUrl.trim() || null,
    force_path_style: form.forcePathStyle,
  };

  if (form.accessKeyId.trim()) {
    payload.access_key_id = form.accessKeyId.trim();
  }

  if (form.secretAccessKey.trim()) {
    payload.secret_access_key = form.secretAccessKey.trim();
  }

  return payload;
};

const saveConfig = async () => {
  isSaving.value = true;
  try {
    const { data } = await r2StorageConfigAPI.update(buildPayload());
    syncForm(data.config);
    useAlert(t('R2_STORAGE_SETTINGS.MESSAGES.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) ||
        t('R2_STORAGE_SETTINGS.MESSAGES.SAVE_FAILED')
    );
  } finally {
    isSaving.value = false;
  }
};

const disableConfig = async () => {
  isDisabling.value = true;
  try {
    await r2StorageConfigAPI.delete();
    resetForm();
    useAlert(t('R2_STORAGE_SETTINGS.MESSAGES.DISABLE_SUCCESS'));
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) ||
        t('R2_STORAGE_SETTINGS.MESSAGES.DISABLE_FAILED')
    );
  } finally {
    isDisabling.value = false;
  }
};

onMounted(() => {
  loadConfig();
});
</script>

<template>
  <SettingsLayout
    class="max-w-3xl mx-auto"
    :is-loading="isLoading"
    :loading-message="$t('R2_STORAGE_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('R2_STORAGE_SETTINGS.TITLE')"
        :description="$t('R2_STORAGE_SETTINGS.DESCRIPTION')"
      />
    </template>

    <template #body>
      <div class="grid gap-6">
        <section
          class="rounded-lg border border-n-weak bg-n-solid-1 p-4 text-sm text-n-slate-11"
        >
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p class="mb-1 font-medium text-n-slate-12">
                {{ $t('R2_STORAGE_SETTINGS.STATUS.TITLE') }}
              </p>
              <p>
                {{
                  isEnabled
                    ? $t('R2_STORAGE_SETTINGS.STATUS.ENABLED')
                    : $t('R2_STORAGE_SETTINGS.STATUS.DISABLED')
                }}
              </p>
            </div>
            <span
              class="rounded-md border px-2 py-1 text-xs font-medium"
              :class="
                isEnabled
                  ? 'border-n-teal-7 bg-n-teal-3 text-n-teal-11'
                  : 'border-n-weak bg-n-solid-2 text-n-slate-11'
              "
            >
              {{
                isEnabled
                  ? $t('R2_STORAGE_SETTINGS.STATUS.ACTIVE')
                  : $t('R2_STORAGE_SETTINGS.STATUS.INACTIVE')
              }}
            </span>
          </div>

          <dl v-if="activeConfig" class="mt-4 grid gap-3 sm:grid-cols-2">
            <div>
              <dt class="text-xs text-n-slate-10">
                {{ $t('R2_STORAGE_SETTINGS.STATUS.CONFIG_ID') }}
              </dt>
              <dd class="mt-1 text-n-slate-12">{{ activeConfig.id }}</dd>
            </div>
            <div>
              <dt class="text-xs text-n-slate-10">
                {{ $t('R2_STORAGE_SETTINGS.STATUS.UPDATED_AT') }}
              </dt>
              <dd class="mt-1 text-n-slate-12">
                {{ formattedUpdatedAt }}
              </dd>
            </div>
          </dl>
        </section>

        <SectionLayout
          :title="$t('R2_STORAGE_SETTINGS.FORM.TITLE')"
          :description="$t('R2_STORAGE_SETTINGS.FORM.DESCRIPTION')"
          with-border
        >
          <form class="grid gap-5" @submit.prevent="saveConfig">
            <div class="grid gap-4 sm:grid-cols-2">
              <Input
                v-model="form.bucket"
                required
                :label="$t('R2_STORAGE_SETTINGS.FORM.BUCKET.LABEL')"
                :placeholder="$t('R2_STORAGE_SETTINGS.FORM.BUCKET.PLACEHOLDER')"
              />
              <Input
                v-model="form.endpoint"
                required
                type="url"
                :label="$t('R2_STORAGE_SETTINGS.FORM.ENDPOINT.LABEL')"
                :placeholder="
                  $t('R2_STORAGE_SETTINGS.FORM.ENDPOINT.PLACEHOLDER')
                "
              />
              <Input
                v-model="form.region"
                :label="$t('R2_STORAGE_SETTINGS.FORM.REGION.LABEL')"
                :placeholder="$t('R2_STORAGE_SETTINGS.FORM.REGION.PLACEHOLDER')"
                :message="$t('R2_STORAGE_SETTINGS.FORM.REGION.HELP')"
              />
              <Input
                v-model="form.publicBaseUrl"
                type="url"
                :label="$t('R2_STORAGE_SETTINGS.FORM.PUBLIC_BASE_URL.LABEL')"
                :placeholder="
                  $t('R2_STORAGE_SETTINGS.FORM.PUBLIC_BASE_URL.PLACEHOLDER')
                "
                :message="$t('R2_STORAGE_SETTINGS.FORM.PUBLIC_BASE_URL.HELP')"
              />
              <Input
                v-model="form.accessKeyId"
                autocomplete="new-password"
                :required="!hasSavedAccessKey"
                :label="$t('R2_STORAGE_SETTINGS.FORM.ACCESS_KEY_ID.LABEL')"
                :placeholder="
                  hasSavedAccessKey
                    ? $t('R2_STORAGE_SETTINGS.FORM.SECRET_PLACEHOLDER')
                    : $t('R2_STORAGE_SETTINGS.FORM.ACCESS_KEY_ID.PLACEHOLDER')
                "
                :message="
                  hasSavedAccessKey
                    ? $t('R2_STORAGE_SETTINGS.FORM.ACCESS_KEY_ID.SAVED')
                    : ''
                "
              />
              <Input
                v-model="form.secretAccessKey"
                type="password"
                autocomplete="new-password"
                :required="!hasSavedSecretKey"
                :label="$t('R2_STORAGE_SETTINGS.FORM.SECRET_ACCESS_KEY.LABEL')"
                :placeholder="
                  hasSavedSecretKey
                    ? $t('R2_STORAGE_SETTINGS.FORM.SECRET_PLACEHOLDER')
                    : $t(
                        'R2_STORAGE_SETTINGS.FORM.SECRET_ACCESS_KEY.PLACEHOLDER'
                      )
                "
                :message="
                  hasSavedSecretKey
                    ? $t('R2_STORAGE_SETTINGS.FORM.SECRET_ACCESS_KEY.SAVED')
                    : ''
                "
              />
            </div>

            <label class="flex items-start gap-3 text-sm text-n-slate-11">
              <Checkbox v-model="form.forcePathStyle" class="mt-0.5" />
              <span>
                <span class="block font-medium text-n-slate-12">
                  {{ $t('R2_STORAGE_SETTINGS.FORM.FORCE_PATH_STYLE.LABEL') }}
                </span>
                <span class="mt-1 block">
                  {{ $t('R2_STORAGE_SETTINGS.FORM.FORCE_PATH_STYLE.HELP') }}
                </span>
              </span>
            </label>

            <p
              class="rounded-lg bg-n-solid-2 px-3 py-2 text-sm text-n-slate-11"
            >
              {{ $t('R2_STORAGE_SETTINGS.FORM.VERSION_NOTE') }}
            </p>

            <div class="flex flex-wrap gap-2">
              <Button
                type="submit"
                icon="i-lucide-save"
                :is-loading="isSaving"
                :label="$t('R2_STORAGE_SETTINGS.ACTIONS.SAVE')"
              />
              <Button
                v-if="isEnabled"
                type="button"
                ruby
                outline
                icon="i-lucide-power"
                :is-loading="isDisabling"
                :label="$t('R2_STORAGE_SETTINGS.ACTIONS.DISABLE')"
                @click="disableConfig"
              />
            </div>
          </form>
        </SectionLayout>
      </div>
    </template>
  </SettingsLayout>
</template>
